import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import type { ImageCarouselInterstitial } from 'LuxKit/Carousel/BaseImageCarousel'
import { sortBy } from 'lib/array/arrayUtils'
import SolidPlayIcon from 'LuxKit/Icons/solid/SolidPlayIcon'

import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Image from 'components/Common/Image'
import VimeoThumbnail from 'components/Common/Video/VimeoThumbnail'

import type { GallerySection } from './OfferGallerySectionedLightbox'

export const PropertyImageButton = styled(Clickable)`
  aspect-ratio: 4 / 3;
  border-radius: ${rem(12)};
  overflow: hidden;
  position: relative;
  padding: 0;
  background: none;
  border: 2px solid transparent;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    border: 2px solid ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`

const VideoBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: ${rem(20)};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: ${props => props.theme.palette.neutral.default.eight};
  z-index: 1;
`

interface Props {
  section: GallerySection
  interstitials: Array<ImageCarouselInterstitial>
  onImageClick: (event: React.MouseEvent<HTMLElement>) => void
  getImageKey: (image: App.Image, index: number) => string
  getCarouselIndexFromImageIndex: (
    imageIndex: number,
    images: Array<App.Image>,
    interstitials: Array<ImageCarouselInterstitial>,
  ) => number
}

function OfferGalleryPropertyImages(props: Props) {
  const intl = useIntl()
  const {
    section,
    interstitials,
    onImageClick,
    getImageKey,
    getCarouselIndexFromImageIndex,
  } = props

  const firstVideoInterstitial = interstitials.find(interstitial => interstitial.id.startsWith('video-'))
  const firstVideoId = firstVideoInterstitial?.id.replace('video-', '')

  const carouselVideoIndex = (() => {
    if (!firstVideoInterstitial) return undefined

    const allItems = [
      ...interstitials.map(interstitial => ({
        id: interstitial.id,
        position: interstitial.position,
        type: 'video' as const,
      })),
      ...section.images.map((image, index) => ({
        id: getImageKey(image, index),
        position: index + 1,
        type: 'image' as const,
      })),
    ]

    const sortedItems = sortBy(allItems, item => {
      if (item.position === 'end') {
        return 10000
      } else if (item.position === 'start') {
        return -1
      } else {
        return item.position
      }
    }, 'asc')

    return sortedItems.findIndex(item => item.type === 'video' && item.id === firstVideoInterstitial.id)
  })()

  return (
    <>
      {firstVideoInterstitial && firstVideoId && carouselVideoIndex !== undefined && carouselVideoIndex >= 0 && (
        <PropertyImageButton
          key={firstVideoInterstitial.id}
          data-section-id={section.id}
          data-image-index={carouselVideoIndex}
          onClick={onImageClick}
          aria-label={intl.formatMessage({
            id: 'uc_MPUoSfLuhOufMoO5B',
            defaultMessage: 'View video',
            description: 'Aria label for button to view property video in gallery',
          })}
        >
          <VimeoThumbnail
            vimeoVideoId={firstVideoId}
            alt={intl.formatMessage({
              id: 'WG01l0ASaszrrw_vlA1g',
              defaultMessage: 'Video thumbnail',
              description: 'Alt text for video thumbnail in gallery in the image gallery or photo section',
            })}
          />
          <VideoBadge aria-hidden="true">
            <SolidPlayIcon />
          </VideoBadge>
        </PropertyImageButton>
      )}
      {section.images.map((image, index) => (
        <PropertyImageButton
          key={getImageKey(image, index)}
          data-section-id={section.id}
          data-image-index={getCarouselIndexFromImageIndex(index, section.images, interstitials)}
          onClick={onImageClick}
        >
          {image.id && <ResponsiveImage id={image.id} fit="center" />}
          {image.url && <Image image={image} fit="center" />}
        </PropertyImageButton>
      ))}
    </>
  )
}

export default OfferGalleryPropertyImages
